import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { translations } from '../i18n';
import { fetchRates, fetchSettings, fetchDevelopers, fetchProjects, fetchAgents } from '../api';

const AppContext = createContext(null);

export const CURRENCIES = {
  AED: { code: 'AED', symbol: 'AED', label: 'UAE Dirham' },
  USD: { code: 'USD', symbol: '$', label: 'US Dollar' },
  EUR: { code: 'EUR', symbol: '\u20AC', label: 'Euro' },
  USDT: { code: 'USDT', symbol: 'USDT', label: 'Tether (USDT)' },
};

export const LANGUAGES = [
  { code: 'en', label: 'English', flag: '\uD83C\uDDEC\uD83C\uDDE7', dir: 'ltr' },
  { code: 'ar', label: '\u0627\u0644\u0639\u0631\u0628\u064A\u0629', flag: '\uD83C\uDDE6\uD83C\uDDEA', dir: 'rtl' },
  { code: 'pt', label: 'Portugu\u00EAs', flag: '\uD83C\uDDE7\uD83C\uDDF7', dir: 'ltr' },
  { code: 'es', label: 'Espa\u00F1ol', flag: '\uD83C\uDDEA\uD83C\uDDF8', dir: 'ltr' },
  { code: 'fr', label: 'Fran\u00E7ais', flag: '\uD83C\uDDEB\uD83C\uDDF7', dir: 'ltr' },
  { code: 'de', label: 'Deutsch', flag: '\uD83C\uDDE9\uD83C\uDDEA', dir: 'ltr' },
  { code: 'ru', label: '\u0420\u0443\u0441\u0441\u043A\u0438\u0439', flag: '\uD83C\uDDF7\uD83C\uDDFA', dir: 'ltr' },
  { code: 'hi', label: '\u0939\u093F\u0928\u094D\u0926\u0940', flag: '\uD83C\uDDEE\uD83C\uDDF3', dir: 'ltr' },
  { code: 'zh', label: '\u4E2D\u6587', flag: '\uD83C\uDDE8\uD83C\uDDF3', dir: 'ltr' },
];

const DEFAULT_RATES = { AED: 1, USD: 0.2723, EUR: 0.2512, USDT: 0.2723 };

const DEFAULT_SETTINGS = {
  name: 'GOBRA Capital Real Estate',
  phone: '+971561822002',
  whatsapp: '+971561822002',
  email: 'diretoria@dhayglysthvianna.com',
  address: 'Downtown Dubai, United Arab Emirates',
  instagram: 'https://instagram.com/gobracapital.realestate',
  instagramHandle: '@gobracapital.realestate',
  heroSlides: [],
  stats: [],
};

export const AppProvider = ({ children }) => {
  const [lang, setLang] = useState(() => localStorage.getItem('gobra_lang') || 'en');
  const [currency, setCurrency] = useState(() => localStorage.getItem('gobra_currency') || 'AED');
  const [rates, setRates] = useState(DEFAULT_RATES);
  const [settings, setSettings] = useState(DEFAULT_SETTINGS);
  const [developers, setDevelopers] = useState([]);
  const [projects, setProjects] = useState([]);
  const [agents, setAgents] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [shortlist, setShortlist] = useState(() => {
    try { return JSON.parse(localStorage.getItem('gobra_shortlist') || '[]'); } catch { return []; }
  });

  useEffect(() => {
    localStorage.setItem('gobra_lang', lang);
    const dir = LANGUAGES.find((l) => l.code === lang)?.dir || 'ltr';
    document.documentElement.dir = dir;
    document.documentElement.lang = lang;
  }, [lang]);

  useEffect(() => { localStorage.setItem('gobra_currency', currency); }, [currency]);
  useEffect(() => { localStorage.setItem('gobra_shortlist', JSON.stringify(shortlist)); }, [shortlist]);

  useEffect(() => {
    let mounted = true;
    Promise.allSettled([fetchRates(), fetchSettings(), fetchDevelopers(), fetchProjects(), fetchAgents()])
      .then(([r, s, d, p, a]) => {
        if (!mounted) return;
        if (r.status === 'fulfilled' && r.value?.rates) setRates(r.value.rates);
        if (s.status === 'fulfilled' && s.value?.name) setSettings({ ...DEFAULT_SETTINGS, ...s.value });
        if (d.status === 'fulfilled' && Array.isArray(d.value)) setDevelopers(d.value);
        if (p.status === 'fulfilled' && Array.isArray(p.value)) setProjects(p.value);
        if (a.status === 'fulfilled' && Array.isArray(a.value)) setAgents(a.value);
        setLoaded(true);
      });
    return () => { mounted = false; };
  }, []);

  const t = useCallback((key) => {
    const dict = translations[lang] || translations.en;
    return dict[key] || translations.en[key] || key;
  }, [lang]);

  const formatPrice = useCallback((aedValue) => {
    const c = CURRENCIES[currency];
    const rate = rates[currency] || 1;
    const converted = (aedValue || 0) * rate;
    let display;
    if (converted >= 1_000_000) display = (converted / 1_000_000).toFixed(converted % 1_000_000 === 0 ? 0 : 1) + 'M';
    else if (converted >= 1000) display = Math.round(converted / 1000) + 'K';
    else display = Math.round(converted).toString();
    return `${c.symbol} ${display}`;
  }, [currency, rates]);

  const toggleShortlist = useCallback((id) => {
    setShortlist((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);
  }, []);

  const refreshData = useCallback(async () => {
    const [d, p, s] = await Promise.allSettled([fetchDevelopers(), fetchProjects(), fetchSettings()]);
    if (d.status === 'fulfilled') setDevelopers(d.value);
    if (p.status === 'fulfilled') setProjects(p.value);
    if (s.status === 'fulfilled' && s.value?.name) setSettings({ ...DEFAULT_SETTINGS, ...s.value });
  }, []);

  const value = {
    lang, setLang, currency, setCurrency, t, formatPrice, rates,
    settings, developers, projects, agents, loaded, refreshData,
    shortlist, toggleShortlist,
  };
  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
};

export const useApp = () => {
  const ctx = useContext(AppContext);
  if (!ctx) throw new Error('useApp must be used within AppProvider');
  return ctx;
};
